import { tryParseDate } from '@shared/functions/try-parse-date';
import { BcmWorkingTimeModel, BcmWorkingTimeModelDto } from '@shared/models/bcm-working-time-model';

export interface BcmEmployeeWorkingTimeModelAssignmentDto {
    id?: number;
    bcm_employees_id?: number;
    working_time_model_id: number;
    valid_from: string;
    valid_to?: string | null;
    workingTimeModel?: BcmWorkingTimeModelDto | null;
}

export class BcmEmployeeWorkingTimeModelAssignment {
    id?: number;
    employeeId?: number;
    workingTimeModelId: number;
    validFrom: Date | null;
    validTo: Date | null;
    workingTimeModel: BcmWorkingTimeModel | null;

    constructor(dto: BcmEmployeeWorkingTimeModelAssignmentDto = {} as BcmEmployeeWorkingTimeModelAssignmentDto) {
        this.id = dto.id;
        this.employeeId = dto.bcm_employees_id;
        this.workingTimeModelId = dto.working_time_model_id;
        this.validFrom = tryParseDate(dto.valid_from);
        this.validTo = tryParseDate(dto.valid_to);
        this.workingTimeModel = dto.workingTimeModel ? new BcmWorkingTimeModel(dto.workingTimeModel) : null;
    }

    toPayload(): Record<string, unknown> {
        return {
            id: this.id,
            working_time_model_id: this.workingTimeModelId,
            valid_from: this.validFrom,
            valid_to: this.validTo,
        };
    }
}

export function formatSqlDateValue(value: Date | null | undefined): string | null {
    if (!value) {
        return null;
    }

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

export function monthStartFromDateValue(value: Date | string | null | undefined): Date | null {
    if (!value) {
        return null;
    }

    const date = value instanceof Date ? value : tryParseDate(value);

    if (!date) {
        return null;
    }

    return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function dayBeforeDateValue(value: Date | null | undefined): Date | null {
    if (!value) {
        return null;
    }

    const date = new Date(value.getTime());
    date.setDate(date.getDate() - 1);

    return date;
}

export function dayAfterDateValue(value: Date | null | undefined): Date | null {
    if (!value) {
        return null;
    }

    const date = new Date(value.getTime());
    date.setDate(date.getDate() + 1);

    return date;
}

export function isDayBeforeMonthStartDateValue(value: Date | null | undefined): boolean {
    return isMonthStartDateValue(dayAfterDateValue(value));
}

export function addMonthsMonthStart(value: Date, months: number): Date {
    return new Date(value.getFullYear(), value.getMonth() + months, 1);
}

export function isMonthStartDateValue(value: Date | null | undefined): boolean {
    return !!value && value.getDate() === 1;
}

export function findAssignmentForMonth(
    assignments: BcmEmployeeWorkingTimeModelAssignment[],
    year: number,
    month: number,
): BcmEmployeeWorkingTimeModelAssignment | null {
    const monthStart = formatSqlDateValue(new Date(year, month - 1, 1));

    if (!monthStart) {
        return null;
    }

    return assignments.find((assignment) => {
        const validFrom = formatSqlDateValue(assignment.validFrom);
        const validTo = formatSqlDateValue(assignment.validTo) ?? '9999-12-31';

        if (!validFrom) {
            return false;
        }

        return monthStart >= validFrom && monthStart <= validTo;
    }) ?? null;
}
